// The answer control for "match" questions (22 Oct 2026, Year 2): each item
// (words, or a picture such as a clock) is joined to its partner (words,
// such as "half past 3"). No dragging and no lines to draw: every item is a
// card with all the partners as buttons under it, and a tap joins the two.
// A partner can be joined to one item only: tapping it under another item
// moves it there. Nothing on the screen says which joins are right.
// Wording for the owner's approval (docs/year-2-answer-types-wording.md).
import { MathText } from '../../components/MathText';
import { PracticeDiagramView } from '../../platform/content/practice-diagram';
import {
  splitMatchChoices,
  toggleMatch,
  type MatchQuestion,
} from '../../platform/content/matchAnswer';

type Props = {
  question: MatchQuestion;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
};

export function MatchAnswer({ question, value, disabled, onChange }: Props) {
  const choices = splitMatchChoices(question, value);
  const joinedTo = new Map(
    Object.entries(choices).map(([itemId, partnerId]) => [partnerId, itemId]),
  );
  return (
    <div className="grid gap-3">
      <p className="font-semibold">Join each one to its partner.</p>
      {question.left.map((item, index) => {
        const nameId = `practice-match-${item.id}`;
        return (
          // A named group of toggle buttons: "5 + 4: 7, 9, 12".
          <div
            key={item.id}
            role="group"
            aria-labelledby={nameId}
            className="grid gap-2.5 rounded-xl border-2 border-line bg-white p-3"
          >
            <div id={nameId} className="text-lg font-semibold text-ink">
              {item.diagram ? (
                <span className="flex flex-col gap-2">
                  {item.label === '' && (
                    <span className="sr-only">
                      Picture {String.fromCharCode(65 + index)}.
                    </span>
                  )}
                  <span className="mx-auto block w-full max-w-[12rem]">
                    <PracticeDiagramView diagram={item.diagram} />
                  </span>
                  {item.label !== '' && <MathText text={item.label} />}
                </span>
              ) : (
                <MathText text={item.label} />
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {question.right.map((partner) => {
                const chosen = choices[item.id] === partner.id;
                // Joined to another item: still there to tap (it moves
                // here), but drawn dashed so the child sees it is taken.
                const taken = !chosen && joinedTo.has(partner.id);
                return (
                  <button
                    key={partner.id}
                    type="button"
                    aria-pressed={chosen}
                    disabled={disabled}
                    className={`flex min-h-12 min-w-0 items-center gap-2 rounded-xl border-2 px-3 py-2 text-left text-base font-semibold [overflow-wrap:anywhere] transition-colors ${
                      chosen
                        ? 'border-forest-600 bg-sage text-forest-800'
                        : taken
                          ? 'border-dashed border-line-strong bg-paper-2 text-muted'
                          : 'border-line bg-white text-ink hover:border-line-strong'
                    }`}
                    onClick={() => {
                      onChange(
                        toggleMatch(question, value, item.id, partner.id),
                      );
                    }}
                  >
                    {/* Not colour alone: an empty ring, or a filled one
                        with a tick. */}
                    <span
                      aria-hidden="true"
                      className={`flex size-6 shrink-0 items-center justify-center rounded-full border-2 ${
                        chosen
                          ? 'border-forest-600 bg-forest-600 text-white'
                          : 'border-line-strong bg-white'
                      }`}
                    >
                      {chosen && (
                        <svg
                          viewBox="0 0 16 16"
                          className="size-4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M3.5 8.5l3 3 6-7" />
                        </svg>
                      )}
                    </span>
                    <span className="min-w-0">
                      <MathText text={partner.label} />
                      {taken && (
                        <span className="sr-only">, already joined</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
